// Shared helpers for the optional hierarchical "advanced" client layout.
// A LayoutNode is { id, title, type?: "group"|"service", children?: LayoutNode[] }.
// A node's dot-joined key is its ancestor ids + its own id, e.g. "plant1.breakfast".

export function slugify(text) {
  return (text || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "node";
}

// Generates an id from title that is unique among the given sibling nodes.
export function generateUniqueId(title, siblings = [], excludeId = null) {
  const base = slugify(title);
  const taken = new Set(
    siblings.filter((s) => s.id !== excludeId).map((s) => s.id),
  );
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
}

// Returns every leaf ("service") node's dot-joined key, in tree order.
export function getLeafKeys(layout) {
  const keys = [];
  const walk = (nodes, prefix) => {
    (nodes || []).forEach((node) => {
      const key = prefix ? `${prefix}.${node.id}` : node.id;
      if (node.children && node.children.length > 0) {
        walk(node.children, key);
      } else {
        keys.push(key);
      }
    });
  };
  walk(layout, "");
  return keys;
}

// "Additional Orders" leaves hold a free-text note rather than a headcount,
// so they're exempt from the Number() coercion every other leaf gets.
const isFreeTextKey = (key) => key.split(".").pop() === "additional-orders";

// Builds a { [key]: count } map from every leaf in the layout, defaulting to 0,
// then overlays whatever counts already exist in layoutEntries.
export function buildCountsFromEntries(layout, layoutEntries = []) {
  const counts = {};
  getLeafKeys(layout).forEach((key) => {
    counts[key] = 0;
  });
  (layoutEntries || []).forEach((entry) => {
    if (entry && entry.key in counts) {
      counts[entry.key] = isFreeTextKey(entry.key)
        ? entry.count ?? ""
        : Number(entry.count) || 0;
    }
  });
  return counts;
}

// Flattens a { [key]: count } map back into layoutEntries[], limited to keys
// that are still real leaves in the current layout.
export function flattenCountsToEntries(layout, counts = {}) {
  return getLeafKeys(layout).map((key) => ({
    key,
    count: isFreeTextKey(key) ? counts[key] ?? "" : Number(counts[key]) || 0,
  }));
}

